import "./styles.css";
import { HeaderData, TableRowData } from "../../types/dataType";
import {
    HEADER_ITEM_CLASS_NAME,
    INCOMPLETE_DOCUMENT_CLASS_NAME,
    LOG_PAGE_WRAPPER_CLASS_NAME,
    LOG_TABLE_WRAPPER_CLASS_NAME,
    OUT_OF_REVIEW_DATE_CELL,
    PAGE_WRAPPER_CLASS_NAME,
} from "./constants";
import {
    TABLE_HEADER_FIELDS,
    TABLE_HEADER_FIELDS_WITH_OPEN_DATE,
    TABLE_HEADER_FIELDS_WITH_REVIEW_DATE,
    TABLE_HEADER_FIELDS_WITH_REVIEW_DATE_AND_OPEN_DATE,
    CENTER_HEADER,
    BLANK_STRING,
    LOG_TABLE_ID,
} from "../../constants/appConstants";
import Table from "react-bootstrap/Table";
import { checkIfDateIsOutdatedToday } from "../../utils/commonUtils";
import { updateSOPDocumentStatusAPI } from "../../apiRequests/getTimelyReviewSOPS/updateSOPDocumentStatusAPI";
import { useEffect, useState } from "react";

interface Props {
    rows: TableRowData[];
    setTimelyReviewKPIData: (data: TableRowData[]) => void;
}

const TimelyCompletionSOPReviewTable = (props: Props) => {
    const [rows, setRows] = useState<TableRowData[]>(props.rows);
    useEffect(() => {
        setRows(props.rows);
    }, [props.rows]);
    const renderLogPage = () => {
        return <div className={LOG_PAGE_WRAPPER_CLASS_NAME}>{renderLogTable()}</div>;
    };

    const renderLogTable = () => {
        return (
            <div className={LOG_TABLE_WRAPPER_CLASS_NAME}>
                <Table striped bordered hover id={LOG_TABLE_ID}>
                    {generateTableHeader(true, false)}
                    {generateTableBoby(true, false, rows)}
                </Table>
            </div>
        );
    };

    const sortDateAcs = (date1InString: string, date2InString: string): number => {
        let res = 0;
        const date1InNumber = convertDateInStringToNumber(date1InString);
        const date2InNumber = convertDateInStringToNumber(date2InString);
        if (date1InNumber < date2InNumber) {
            res = -1;
        }
        if (date1InNumber > date2InNumber) {
            res = 1;
        }
        return res;
    };

    const convertDateInStringToNumber = (dateInString: string): number => {
        if (dateInString === BLANK_STRING) return 0;
        try {
            let d = dateInString.split("/");
            const year = d[2];
            const day = d[1];
            const month = d[0];
            let date = new Date(year + "/" + month + "/" + day);
            return date.getTime();
        } catch (ex) {
            return 0;
        }
    };

    const generateTableBoby = (isShowReviewColumn: boolean, isShowOpenDate: boolean, tableData: TableRowData[]) => {
        let pageIndex = 0;
        return (
            <tbody>
                {tableData.map((page) => {
                    pageIndex++;
                    return getTableRow(page, pageIndex, isShowReviewColumn, isShowOpenDate);
                })}
            </tbody>
        );
    };

    const getTableRow = (rowData: TableRowData, pageIndex: number, isShowReviewColumn: boolean, isShowOpenDate: boolean) => {
        const isPageReviewed = rowData.isPageReviewed;
        return (
            <tr key={pageIndex} className={isPageReviewed ? BLANK_STRING : INCOMPLETE_DOCUMENT_CLASS_NAME}>
                {createCenterTextCell(pageIndex.toString())}
                {createCellWithLink(rowData.documentCode, rowData.link)}
                {createCellWithLink(rowData.documentName, rowData.link)}
                {createCenterTextCell(rowData.latestVersion)}
                {createCell(rowData.effectiveOn)}
                {isShowReviewColumn && createCell(rowData.reviewDate, checkIfDateIsOutdatedToday(rowData.reviewDate))}
                {isShowOpenDate && createCell(rowData.openDate)}
                {createCenterTextCell(rowData.effectiveRevision)}
                {createCenterTextCell(rowData.latestRevision)}
                {renderRowAction(rowData)}
            </tr>
        );
    };
    const renderRowAction = (rowData: TableRowData) => {
        return (
            <td>
                <div>
                    {(rowData.isStatusUpdated || rowData.status === "FAILED") && (
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                updateSOPDocumentStatus(rowData.documentId, rowData.status === "FAILED" ? "PASSED" : "FAILED");
                            }}
                        >
                            Mark as {rowData.status === "FAILED" ? "Passed" : "Failed"}
                        </button>
                    )}
                </div>
            </td>
        );
    };
    const updateSOPDocumentStatus = async (documentId: string, status: string) => {
        const document = await updateSOPDocumentStatusAPI(documentId, status);
        const newData = rows.map((row) => {
            if (row.documentId === documentId) {
                return {
                    ...row,
                    status: document.status,
                    isStatusUpdated: true,
                    isPageReviewed: document.status === "PASSED",
                };
            }
            return row;
        });
        setRows(newData);
        props.setTimelyReviewKPIData(newData);
    };

    const checkIsInCompleteDocument = (rowData: TableRowData) => {
        return (
            rowData.effectiveOn === BLANK_STRING ||
            rowData.effectiveRevision.toLocaleLowerCase().trim() !== rowData.latestRevision.toLocaleLowerCase().trim()
        );
    };

    const createCellWithLink = (text: string, link: string) => {
        return (
            <td>
                <a href={link} target="_blank">
                    <p>{text}</p>
                </a>
            </td>
        );
    };

    const createCenterTextCell = (content: string) => {
        return <td>{createCenterDiv(content)}</td>;
    };

    const createCell = (content: string, isOutOfReviewDate?: boolean) => {
        return <td className={isOutOfReviewDate ? OUT_OF_REVIEW_DATE_CELL : BLANK_STRING}>{content}</td>;
    };

    const createCenterDiv = (content: string) => {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {content}
            </div>
        );
    };

    const generateTableHeader = (isShowReviewColumn: boolean, isShowOpenDate: boolean) => {
        let headerData: HeaderData[] = TABLE_HEADER_FIELDS;
        if (isShowReviewColumn) headerData = TABLE_HEADER_FIELDS_WITH_REVIEW_DATE;
        if (isShowOpenDate) headerData = TABLE_HEADER_FIELDS_WITH_OPEN_DATE;
        if (isShowOpenDate && isShowReviewColumn) headerData = TABLE_HEADER_FIELDS_WITH_REVIEW_DATE_AND_OPEN_DATE;

        return (
            <thead>
                <tr>
                    {headerData.map((item) => {
                        return (
                            <th key={item.dataField + item.text} style={{ width: item.width + "%" }} data-colwidth={item.width}>
                                <div className={HEADER_ITEM_CLASS_NAME}>
                                    {CENTER_HEADER.includes(item.text) ? createCenterDiv(item.text) : item.text}
                                </div>
                            </th>
                        );
                    })}
                    <th>Action</th>
                </tr>
            </thead>
        );
    };

    return <div className={PAGE_WRAPPER_CLASS_NAME}>{renderLogPage()}</div>;
};

export default TimelyCompletionSOPReviewTable;
